import { gql } from "lib/apolloClient";
import {
  participantsStudentFragments,
  participantsStaffFragments,
  participantsFamilyMemberFragments,
} from "./ParticipantsFragments";

// ---------------------------------------------------------------------------
// Shape-asymmetry reference — consuming hooks must handle these differences:
//
//   getGradeDetailsBatchQuery   → data.multiNode          (flat array of Grade objects)
//   searchGradesByIdsQuery      → data.node.grades        (flat array of Grade objects)
//   getCourseDetailsBatchQuery  → data.multiNode          (flat array of Course objects)
//   searchCoursesByIdsQuery     → data.node.courses.edges[].node  (paginated connection)
//
// useGradeBatchDetails and useCourseBatchDetails must normalise accordingly.
// ---------------------------------------------------------------------------

// Batch-fetches full details for a set of grade IDs.
// Uses multiNode which is the canonical pattern for fetching multiple entities
// by ID in this codebase (see CourseQuery.js getGradeDetailsQuery,
// NotificationSettings/modules/Query.js getGradesByIdsQuery).
export const getGradeDetailsBatchQuery = gql`
  query getGradeDetailsBatch($ids: [ID!]!) {
    multiNode(ids: $ids, type: GRADE) {
      id
      ... on Grade {
        id
        name
        staffCount
        studentCount
        familyMemberCount
        curriculumProgram {
          id
          label
          acronym
        }
      }
    }
  }
`;

// Batch-fetches full details for a set of course IDs.
// Uses multiNode(type: COURSE) — same pattern as getOrganizationCoursesUserCountQuery
// in EventModalV2Query.js.
// NOTE: The backend field for course teachers is `collaborator` (singular). We alias it
// to `collaborators` here so that useCourseBatchDetails exposes the expected client-side
// shape (item.collaborators.totalCount) to its consumers. The alias bridges the backend
// field name to the shape the hook's callers depend on.
export const getCourseDetailsBatchQuery = gql`
  query getCourseDetailsBatch($ids: [ID!]!) {
    multiNode(ids: $ids, type: COURSE) {
      id
      ... on Course {
        id
        title
        students {
          totalCount
        }
        collaborators: collaborator {
          totalCount
        }
        familyMembers {
          totalCount
        }
      }
    }
  }
`;

// Searches grades within a scoped set of IDs.
// Uses the org-level grades connection with a top-level `ids` argument (proven
// in MessagingQuery.js: grades(ids: $gradeIds, filters: $filters)) and passes
// searchText through GradeFilterInput.
// NOTE: Requires $organizationId to scope to the correct org node. The consuming
// hook (useGradeBatchDetails) must source organizationId from Redux state via
// getOrganizationId() — same as useParticipantsData.js line ~137.
export const searchGradesByIdsQuery = gql`
  query searchGradesByIds(
    $organizationId: ID!
    $ids: [ID!]!
    $searchText: String!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        grades(ids: $ids, filters: { searchText: $searchText }) {
          id
          name
          staffCount
          studentCount
          familyMemberCount
          curriculumProgram {
            id
            label
            acronym
          }
        }
      }
    }
  }
`;

// Searches courses within a scoped set of IDs.
// Uses the org-level courses connection with CourseFilterInput.
// NOTE: Requires $organizationId to scope to the correct org node. The consuming
// hook (useCourseBatchDetails) must source organizationId from Redux state via
// getOrganizationId() — same as useParticipantsData.js line ~137.
// NOTE: `ids` is intentionally omitted from CourseFilterInput — no existing
// codebase query passes ids inside that input type and backend confirmation is
// not available. useCourseBatchDetails must post-filter results client-side by
// the known course IDs after receiving the response.
// NOTE: The `collaborator` field is aliased to `collaborators` to match the
// client-side shape expected by useCourseBatchDetails consumers
// (item.collaborators.totalCount).
export const searchCoursesByIdsQuery = gql`
  query searchCoursesByIds($organizationId: ID!, $searchText: String!) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(filters: { searchText: $searchText }) {
          edges {
            node {
              id
              title
              students {
                totalCount
              }
              collaborators: collaborator {
                totalCount
              }
              familyMembers {
                totalCount
              }
            }
          }
        }
      }
    }
  }
`;

// Batch-fetches user details for a known set of IDs using the org-level
// calendarEventItemUsers connection. Handles Student, Staff, and FamilyMember
// in a single query — replacing the three separate multiNode/node paths.
export const getCalendarEventItemUsersBatch = gql`
  query getCalendarEventItemUsersBatch(
    $organizationId: ID!
    $filters: CalendarEventItemUsersFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        calendarEventItemUsers(
          orderBy: FULL_NAME
          orderByDirection: ASC
          filters: $filters
        ) {
          edges {
            node {
              id
              type
              profileImage
              firstName
              middleName
              lastName
              ... on Student {
                preferredName
                alertTypes {
                  id
                  name
                  icon
                }
                yearGroup {
                  id
                  grade {
                    id
                    name
                  }
                }
              }
              ... on Staff {
                preferredName
              }
              ... on FamilyMember {
                preferredName
                children {
                  id
                  firstName
                  middleName
                  lastName
                  preferredName
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getParticipantStudentsQuery = gql`
  query getParticipantStudents(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: OrgConnectionFilterInput
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        students(
          first: $first
          after: $after
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          totalCount
          edges {
            node {
              ...participantStudentBasicDetailsItem
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${participantsStudentFragments.basicDetails}
`;

export const getParticipantStaffQuery = gql`
  query getParticipantStaff(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: OrgConnectionFilterInput
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        staff(
          first: $first
          after: $after
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          totalCount
          edges {
            node {
              ...participantStaffBasicDetailsItem
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${participantsStaffFragments.basicDetails}
`;

export const getParticipantFamilyMembersQuery = gql`
  query getParticipantFamilyMembers(
    $organizationId: ID!
    $first: Int
    $after: String
    $familyMembersFilter: FamilyMembersFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        familyMembers(
          first: $first
          after: $after
          filters: $familyMembersFilter
        ) {
          totalCount
          edges {
            node {
              ...participantFamilyMemberBasicDetailsItem
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${participantsFamilyMemberFragments.basicDetails}
`;
